import {
  impersonationRef,
  useImpersonationRefValue,
} from "@client/impersonationRef";
import { trpc } from "@client/trpc";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { ROUND_VISIT_HISTORY_LIMIT } from "@lib/todoRailTypes";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { videoStateQueryInput } from "@client/utils/videoStateQueryInput";
import { applyProgressFlushResult } from "@client/components/video/videoStatesProgressSync";
import { todoChecklistQueryInput } from "./todoQueryInput";
import {
  createChecklistMarkSync,
  createVideoCompletionSync,
  createVisitSync,
  type ChecklistMarkPayload,
  type VideoCompletionPayload,
  type VisitPayload,
} from "./todoRailSync";

interface TodoRailSyncApi {
  /** Absent while viewing as someone else — that progress is not ours to move. */
  enqueueMark?: (payload: ChecklistMarkPayload) => void;
  enqueueVisit: (payload: VisitPayload) => void;
  /** Absent while viewing as someone else — see `enqueueMark`. */
  enqueueVideoCompletion?: (payload: VideoCompletionPayload) => void;
}

/**
 * Null until the provider mounts.
 *
 * During a View-As session it is NOT null, but it carries visits only:
 * `enqueueMark` and `enqueueVideoCompletion` are absent, and the hooks treat
 * that as "don't write" — the third impersonation guard for the funder's
 * progress (the server no-ops, the callers check, the outbox is never created).
 * Visits are the exception because they are the VIEWER's, not the funder's; see
 * `recordRoundVisit`.
 */
const TodoRailSyncContext = createContext<TodoRailSyncApi | null>(null);

export function useTodoRailSync(): TodoRailSyncApi | null {
  return useContext(TodoRailSyncContext);
}

/**
 * Owns the To Do rail's durable write path.
 *
 * Mounted once per round so the queues start and stop with it.
 *
 * The PROGRESS features (marks, video completion) are created only for a
 * non-impersonated session, so queued real-user writes can never flush under an
 * acting-as header — the same construction the video progress outbox uses, and
 * the reason `shouldCommit` reads the live ref rather than a mount-time value.
 *
 * The VISIT feature is created either way, and its queue is keyed by the real
 * user, because History belongs to whoever is at the keyboard rather than to the
 * funder on screen. The server scopes the row the same way, so a visit
 * delivered under an acting-as header still lands in the viewer's own history.
 */
export function TodoRailSyncProvider({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const utils = trpc.useUtils();
  const { data: currentUser } = useCurrentUser();
  const realUserId = currentUser?.userId ?? null;
  // State, not a ref: the context value has to reach consumers, and a ref
  // assignment in an effect never re-renders them.
  const [api, setApi] = useState<TodoRailSyncApi | null>(null);
  const viewAsUserId = useImpersonationRefValue();

  useEffect(() => {
    // No real user yet means no queue to key: the outbox is per person.
    if (realUserId == null) {
      setApi(null);
      return;
    }

    const viewingAsSomeoneElse = viewAsUserId != null;
    // Guards the funder's PROGRESS writes only. A visit is the viewer's own and
    // commits either way.
    const progressShouldCommit = () => impersonationRef.current == null;
    // The cache entry the drawer is actually reading, which is keyed by who is
    // being viewed — so a visit landing during a View-As session patches the
    // list on screen rather than an entry nobody is looking at.
    const queryInput = todoChecklistQueryInput(roundId, viewAsUserId);
    // History carries no impersonation discriminator — one entry per round,
    // because the server scopes it to the real user either way.
    const historyInput = { roundId };

    /**
     * A drained entry can belong to a round OTHER than the mounted one — the
     * queues are per user, and a funder can leave a round before its writes
     * flush. Nothing here can patch that round's cache (it isn't rendered, and
     * for visits there is no optimistic row to correct), so mark it stale: on
     * return, React Query would otherwise serve a fresh-window cache that
     * predates the write, hiding a recorded History entry and letting "Pick up
     * where you left off" point at something older. Same rule the video
     * progress outbox applies to its own source round.
     *
     * Each feature passes the invalidation for the cache IT writes: marks live
     * in the checklist entry (keyed by who is being viewed), visits in the
     * history entry (keyed by round alone). Sending both to one cache was how a
     * foreign-round drain during a View-As session marked an entry nobody was
     * reading and left the one on screen stale.
     */
    const isOtherRound = (
      payloadRoundId: string,
      invalidateOtherRound: (otherRoundId: string) => void,
    ) => {
      if (payloadRoundId === roundId) {
        return false;
      }

      invalidateOtherRound(payloadRoundId);
      return true;
    };

    const invalidateOtherRoundChecklist = (otherRoundId: string) => {
      void utils.getRoundChecklist.invalidate(
        todoChecklistQueryInput(otherRoundId, viewAsUserId),
      );
    };

    const invalidateOtherRoundHistory = (otherRoundId: string) => {
      void utils.getRoundVisits.invalidate({ roundId: otherRoundId });
    };

    const marks = createChecklistMarkSync({
      userId: realUserId,
      shouldCommit: progressShouldCommit,
      onSuccess: (result, payload) => {
        if (isOtherRound(payload.roundId, invalidateOtherRoundChecklist)) {
          return;
        }

        if (!result.applied) {
          void utils.getRoundChecklist.invalidate(queryInput);
          return;
        }

        // Nothing cached to patch means the query is still in flight (a
        // persisted write draining during round startup). Returning early
        // discarded the server's answer, and the older in-flight response then
        // populated the cache — making a delivered mark look undone. Invalidate
        // instead, the same way the video-progress outbox does.
        const patchedMark = utils.getRoundChecklist.getData(queryInput);

        if (patchedMark == null || result.current == null) {
          void utils.getRoundChecklist.invalidate(queryInput);
          return;
        }

        utils.getRoundChecklist.setData(queryInput, (prev) => {
          if (prev == null) {
            return prev;
          }

          const rest = prev.marks.filter(
            (mark) => mark.itemKey !== payload.itemKey,
          );
          return { ...prev, marks: [...rest, result.current!] };
        });
      },
    });

    const visits = createVisitSync({
      userId: realUserId,
      onSuccess: (result, payload) => {
        if (isOtherRound(payload.roundId, invalidateOtherRoundHistory)) {
          return;
        }

        // A rejected visit means another device holds a NEWER one for this
        // target — and this device is still ordering History by the older
        // timestamp it optimistically wrote. Refetch rather than leave "Pick up
        // where you left off" pointing backwards until something else happens
        // to invalidate; the mark and completion callbacks do the same.
        if (!result.applied) {
          void utils.getRoundVisits.invalidate(historyInput);
          return;
        }

        if (utils.getRoundVisits.getData(historyInput) == null) {
          void utils.getRoundVisits.invalidate(historyInput);
          return;
        }

        utils.getRoundVisits.setData(historyInput, (prev) => {
          if (prev == null) {
            return prev;
          }

          const targetKey = `${payload.kind}:${payload.targetId}`;
          const rest = prev.visits.filter(
            (visit) => visit.targetKey !== targetKey,
          );
          // SORTED, not prepended. The server orders by `visitedAt` precisely
          // so a queued visit draining late stays in its place; blindly
          // promoting whatever just arrived reintroduced the bug that ordering
          // was added to fix — an offline device reconnecting would push an
          // older place to the top of History and to the Resume card.
          return {
            ...prev,
            visits: [
              {
                targetKey,
                kind: payload.kind,
                targetId: payload.targetId,
                label: payload.label,
                visitedAt: payload.visitedAt,
              },
              ...rest,
            ]
              .sort(
                (a, b) =>
                  new Date(b.visitedAt).getTime() -
                  new Date(a.visitedAt).getTime(),
              )
              .slice(0, ROUND_VISIT_HISTORY_LIMIT),
          };
        });
      },
    });

    const videoCompletion = createVideoCompletionSync({
      userId: realUserId,
      shouldCommit: progressShouldCommit,
      onSuccess: (result, payload) => {
        // Completion lives in the VIDEO STATE cache, so a foreign round's entry
        // marks that cache stale rather than the checklist's.
        if (payload.roundId !== roundId) {
          void utils.getUserVideoState.invalidate(
            videoStateQueryInput(payload.roundId, null),
          );
          return;
        }

        // The rail reads completion out of the video-state cache, so reconcile
        // there — and on a declined write (impersonation) refetch rather than
        // leaving the optimistic tick standing.
        const progressInput = videoStateQueryInput(roundId, null);

        if (!result.applied) {
          void utils.getUserVideoState.invalidate(progressInput);
          return;
        }

        if (utils.getUserVideoState.getData(progressInput) == null) {
          void utils.getUserVideoState.invalidate(progressInput);
          return;
        }

        utils.getUserVideoState.setData(progressInput, (prev) =>
          // The SAME merge the player's own flushes use, not a second copy of
          // it. A completion response carries a whole row — including the
          // server's LAST STORED playhead, older than whatever the player has
          // written since — and every field on it has to be ordered against the
          // cache on its own clock: position by `clientUpdatedAt` (or marking a
          // video complete mid-watch jumped the stripe and "x min left"
          // backwards), the verdict by `completionUpdatedAt` (or the first of
          // two rapid presses undoes the second). Insert-when-absent comes with
          // it: a video marked complete before it was ever opened has no row.
          applyProgressFlushResult(prev, {
            applied: true,
            current: result.current,
          }),
        );
      },
    });

    visits.start();

    if (!viewingAsSomeoneElse) {
      marks.start();
      videoCompletion.start();
    }

    setApi({
      enqueueVisit: (payload) => visits.enqueue(payload),
      // Omitted while viewing as someone else, so a caller cannot write that
      // funder's progress even by mistake — the absence IS the guard.
      ...(viewingAsSomeoneElse
        ? {}
        : {
            enqueueMark: (payload: ChecklistMarkPayload) =>
              marks.enqueue(payload),
            enqueueVideoCompletion: (payload: VideoCompletionPayload) =>
              videoCompletion.enqueue(payload),
          }),
    });

    return () => {
      marks.stop();
      visits.stop();
      videoCompletion.stop();
      // The API deliberately stays live through teardown: a dwell firing as the
      // round unmounts still lands in the DURABLE queue and drains on the next
      // start. Dropping it here would discard exactly the writes the outbox
      // exists to keep — the mistake the video outbox documents having made.
    };
  }, [realUserId, viewAsUserId, roundId, utils]);

  return (
    <TodoRailSyncContext.Provider value={api}>
      {children}
    </TodoRailSyncContext.Provider>
  );
}
